import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from '../config';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0, style = {} }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

export default function Investors() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [heroRef, heroVisible] = useInView(0.05);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      await axios.post(`${API}/api/contact/investors`, form);
      setStatus('sent');
    } catch {
      window.location.href = `mailto:support@minomarkets.app?subject=Investor Enquiry – ${form.company || form.name}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`)}`;
      setStatus('sent');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>Investors — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Mino Markets is building Africa's prediction markets platform. We're early-stage and open to investors." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        /* ── Aurora ── */
        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55,114,255,0.09) 40deg,
            rgba(22,199,132,0.07) 80deg,
            rgba(124,58,237,0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22,199,132,0.06) 50deg,
            rgba(55,114,255,0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }
        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }

        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }

        /* ── Nav ── */
        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        /* ── Form fields ── */
        .inv-field { transition: border-color 0.2s; outline: none; font-family: 'Outfit', sans-serif; }
        .inv-field:focus { border-color: rgba(255,255,255,0.28) !important; }
        .inv-field::placeholder { color: rgba(255,255,255,0.14); }

        /* ── Stat cards ── */
        .inv-stat-card { transition: border-color 0.25s, background 0.25s; }
        .inv-stat-card:hover { border-color: rgba(255,255,255,0.14) !important; background: rgba(255,255,255,0.05) !important; }

        .submit-btn-inv:hover:not(:disabled) { opacity: 0.88 !important; }

        /* ── Scan line on stat numbers ── */
        .inv-num { position: relative; display: inline-block; }
        .inv-num::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(55,114,255,0.5), transparent);
          animation: numUnderline 4s ease-in-out infinite;
        }
        @keyframes numUnderline {
          0%,100% { opacity: 0; transform: scaleX(0); }
          50%      { opacity: 1; transform: scaleX(1); }
        }
      `}</style>

      {/* Background layers */}
      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          {[['Markets', '/markets'], ['About', '/about'], ['Blog', '/blog']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button
          onClick={() => document.getElementById('inv-form').scrollIntoView({ behavior: 'smooth' })}
          style={{ background: '#e8e6e0', color: '#000', border: 'none', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'opacity 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          Get in touch
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Investor Relations</p>
            <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(44px,7vw,86px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 32, maxWidth: 740 }}>
              The future of markets<br />is being built<br /><span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}>in Africa.</span>
            </h1>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.34)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300, marginBottom: 44 }}>
              Kenya's first real-money prediction markets platform — live, transacting, and growing. We're looking for early partners who see what we see.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => document.getElementById('inv-form').scrollIntoView({ behavior: 'smooth' })}
                style={{ background: '#e8e6e0', color: '#000', border: 'none', borderRadius: 10, padding: '14px 30px', fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, cursor: 'pointer', letterSpacing: '-0.1px', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                Partner with us
              </button>
              <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.26)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.26)'}>
                See the platform →
              </span>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Stats */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'start' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>The Opportunity</p>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 600, lineHeight: 1.16, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 24 }}>
                  Africa's most engaged audiences — no platform to match.
                </h2>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.3)', lineHeight: 1.85, fontWeight: 300 }}>
                  Kenya has some of the world's most politically engaged, sports-obsessed, and mobile-native populations. Prediction markets exist globally — but not here. Not yet.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  ['58M+', 'Kenyans with mobile money access via M-PESA'],
                  ['~40%', 'Internet penetration, growing 15% year on year'],
                  ['#1', 'Kenya ranked for mobile money adoption globally'],
                  ['$0', 'Direct prediction market competitors in East Africa'],
                ].map(([stat, label]) => (
                  <div key={stat} className="inv-stat-card" style={{ padding: '20px 24px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: 'rgba(255,255,255,0.025)', display: 'flex', alignItems: 'center', gap: 20 }}>
                    <span className="inv-num" style={{ fontFamily: "'Syne', sans-serif", fontSize: 30, fontWeight: 700, color: '#e8e6e0', letterSpacing: '-1.5px', flexShrink: 0, minWidth: 72 }}>{stat}</span>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.55, fontWeight: 300 }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* What we've built */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>What We've Built</p>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 600, lineHeight: 1.16, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 56, maxWidth: 500 }}>
              Live platform. Real money. Real users.
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {[
                ['Live & transacting', 'Mino is deployed and processing real M-PESA deposits and withdrawals. Not a prototype.'],
                ['M-PESA native', 'Approved PayBill and B2C. Every Kenyan with a SIM card can participate in seconds.'],
                ['Real-time markets', 'Binary and multi-choice markets on politics, sports, and global events.'],
                ['Compliant by design', 'Withholding tax on net gains, responsible trading limits, and identity flows built in from day one.'],
                ['First in class', 'No funded competitor exists in this space in East Africa. The category is open.'],
                ['CMA sandbox path', 'Our roadmap includes approaching the CMA regulatory sandbox within 12 months.'],
              ].map(([title, body], i) => (
                <div key={title} style={{ padding: '36px 30px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', borderRadius: i === 0 ? '16px 0 0 0' : i === 2 ? '0 16px 0 0' : i === 3 ? '0 0 0 16px' : i === 5 ? '0 0 16px 0' : '0', transition: 'background 0.25s, border-color 0.25s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.1px' }}>{title}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.26)', lineHeight: 1.7, fontWeight: 300 }}>{body}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Why now */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'start' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Why Now</p>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 600, lineHeight: 1.16, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 40 }}>
                  The conditions have never been better.
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {[
                    ['Political intensity', "Kenya's 2027 elections are approaching. Political markets are our highest-engagement category."],
                    ['Mobile-first money', 'M-PESA has 90%+ adult penetration. The payment rails were already there — we built on top.'],
                    ['Sports culture', 'Premier League, Formula 1, athletics — the appetite to engage with outcomes is everywhere.'],
                    ['First mover', 'No funded competitor in East Africa. The category is wide open.'],
                  ].map(([h, b]) => (
                    <div key={h} style={{ paddingLeft: 18, borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                      <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: '#e8e6e0', marginBottom: 6 }}>{h}</p>
                      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, fontWeight: 300 }}>{b}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding: '40px 36px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, background: 'rgba(255,255,255,0.025)' }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 32 }}>Who we're looking for</p>
                {[
                  ['Angel investors', 'Early-stage individuals who believe in African fintech.'],
                  ['Strategic partners', 'Operators who see distribution or media synergies.'],
                  ['Seed funds', 'Institutional investors focused on African consumer internet.'],
                  ['Advisors', 'Regulatory, financial, or growth experts.'],
                ].map(([t, d], i, a) => (
                  <div key={t} style={{ marginBottom: i < a.length - 1 ? 24 : 0, paddingBottom: i < a.length - 1 ? 24 : 0, borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: '#e8e6e0', marginBottom: 6 }}>{t}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.65, fontWeight: 300 }}>{d}</p>
                  </div>
                ))}
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', lineHeight: 1.6, marginTop: 28, fontWeight: 300 }}>A pitch deck is available on request after initial contact.</p>
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Contact form */}
        <div id="inv-form" style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Get in Touch</p>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 600, lineHeight: 1.14, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 20 }}>
                  Let's talk about what we're building.
                </h2>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.3)', lineHeight: 1.8, marginBottom: 36, fontWeight: 300 }}>
                  Fill in the form and we'll respond within 48 hours.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <a href="mailto:support@minomarkets.app" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.26)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.26)'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    support@minomarkets.app
                  </a>
                  <a href="https://minomarkets.app" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.26)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.26)'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 1 0 20 14.5 14.5 0 0 1 0-20"/><path d="M2 12h20"/></svg>
                    minomarkets.app
                  </a>
                </div>
              </div>

              <div style={{ padding: '40px 36px', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 18, background: 'rgba(255,255,255,0.025)' }}>
                {status === 'sent' ? (
                  <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e8e6e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#e8e6e0', marginBottom: 10 }}>Message sent.</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, fontWeight: 300 }}>We'll be in touch within 48 hours.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {[
                      { key: 'name',    label: 'Full name',        placeholder: 'David Kamau',   type: 'text'  },
                      { key: 'email',   label: 'Email address',    placeholder: 'david@fund.com', type: 'email' },
                      { key: 'company', label: 'Fund or company',  placeholder: 'Optional',       type: 'text'  },
                    ].map(({ key, label, placeholder, type }) => (
                      <div key={key}>
                        <label style={{ display: 'block', fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.28)', marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
                        <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} className="inv-field"
                          style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8, padding: '11px 14px', fontSize: 14, color: '#e8e6e0' }} />
                      </div>
                    ))}
                    <div>
                      <label style={{ display: 'block', fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.28)', marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Message</label>
                      <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us about your fund and what you're interested in..." rows={4} className="inv-field"
                        style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8, padding: '11px 14px', fontSize: 14, color: '#e8e6e0', resize: 'vertical' }} />
                    </div>
                    <button onClick={handleSubmit} disabled={!form.name || !form.email || !form.message || status === 'sending'} className="submit-btn-inv"
                      style={{ width: '100%', background: (!form.name || !form.email || !form.message) ? 'rgba(255,255,255,0.06)' : '#e8e6e0', color: (!form.name || !form.email || !form.message) ? 'rgba(255,255,255,0.2)' : '#000', border: 'none', borderRadius: 9, padding: '13px', fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, cursor: (!form.name || !form.email || !form.message) ? 'not-allowed' : 'pointer', transition: 'all 0.2s', letterSpacing: '-0.1px' }}>
                      {status === 'sending' ? 'Sending...' : 'Send message'}
                    </button>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.16)', textAlign: 'center', lineHeight: 1.5, fontWeight: 300 }}>Your details are kept strictly confidential.</p>
                  </div>
                )}
              </div>
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · DAFCOM ENTERPRISES</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}